import { create } from "zustand"
import type { AssistantTurnMessage, Proposal } from "./types"

/**
 * The assistant's conversations, one per project, kept in memory only: a
 * reload clears them (nothing is persisted, nothing goes to the server).
 */

export type ProposalStatus = "pending" | "accepted" | "rejected" | "stale"

export interface AssistantMessage {
  /** Unique in the store; proposals point at the reply that brought them. */
  id: number
  role: "user" | "assistant"
  text: string
  /** Part of a turn that failed: shown, but never sent again. */
  failed?: boolean
}

export interface StoredProposal {
  /**
   * The proposal as the converter sent it, except its id: the converter's
   * ids (`p1`, `p2`, ...) restart every turn, so the store prefixes the
   * reply's message id (`m4.p1`) to make it unique in the conversation.
   */
  proposal: Proposal
  status: ProposalStatus
  /** The assistant message the proposal came with. */
  messageId: number
  /** The name a script proposal was saved under, once the user saved it. */
  savedAs?: string
}

export interface Conversation {
  messages: AssistantMessage[]
  proposals: StoredProposal[]
  /** A turn is on its way. */
  busy: boolean
  /** The question being answered while busy, shown at the end of the list. */
  pending: string | null
  /** The panel is open. */
  open: boolean
}

export const EMPTY_CONVERSATION: Conversation = Object.freeze({
  messages: [],
  proposals: [],
  busy: false,
  pending: null,
  open: false,
}) as Conversation

/** At most this many messages go with a turn (the question included). */
export const MAX_SENT_MESSAGES = 20

interface AssistantState {
  conversations: Record<string, Conversation>
  /** The next message id. */
  nextId: number
  setOpen: (projectId: string, open: boolean) => void
  toggleOpen: (projectId: string) => void
  /** Marks a turn as on its way with the question `text`. */
  startTurn: (projectId: string, text: string) => void
  /**
   * Ends a turn that was answered: appends the question and the reply, and
   * the reply's proposals as pending.
   */
  addTurn: (
    projectId: string,
    userText: string,
    reply: string,
    proposals: Proposal[]
  ) => void
  /** Ends a turn that failed: appends the question and the error text, neither sent again. */
  addFailedTurn: (projectId: string, userText: string, errorText: string) => void
  /**
   * Sets the status of the proposal with the store's id `proposalId`, and
   * for a script the user saved, the name it was saved under.
   */
  setProposalStatus: (
    projectId: string,
    proposalId: string,
    status: ProposalStatus,
    savedAs?: string
  ) => void
  /** Forgets the conversation (the panel stays as it is). */
  clear: (projectId: string) => void
}

const conversationOf = (s: AssistantState, projectId: string): Conversation =>
  s.conversations[projectId] ?? EMPTY_CONVERSATION

const withConversation = (
  s: AssistantState,
  projectId: string,
  change: (c: Conversation) => Partial<Conversation>
): Pick<AssistantState, "conversations"> => {
  const current = conversationOf(s, projectId)
  return {
    conversations: {
      ...s.conversations,
      [projectId]: { ...current, ...change(current) },
    },
  }
}

export const useAssistantStore = create<AssistantState>()((set) => ({
  conversations: {},
  nextId: 1,
  setOpen: (projectId, open) =>
    set((s) => withConversation(s, projectId, () => ({ open }))),
  toggleOpen: (projectId) =>
    set((s) => withConversation(s, projectId, (c) => ({ open: !c.open }))),
  startTurn: (projectId, text) =>
    set((s) =>
      withConversation(s, projectId, () => ({ busy: true, pending: text }))
    ),
  addTurn: (projectId, userText, reply, proposals) =>
    set((s) => {
      const userId = s.nextId
      const replyId = s.nextId + 1
      return {
        nextId: s.nextId + 2,
        ...withConversation(s, projectId, (c) => ({
          busy: false,
          pending: null,
          messages: [
            ...c.messages,
            { id: userId, role: "user", text: userText },
            { id: replyId, role: "assistant", text: reply },
          ],
          proposals: [
            ...c.proposals,
            ...proposals.map(
              (p): StoredProposal => ({
                proposal: { ...p, id: `m${replyId}.${p.id}` },
                status: "pending",
                messageId: replyId,
              })
            ),
          ],
        })),
      }
    }),
  addFailedTurn: (projectId, userText, errorText) =>
    set((s) => ({
      nextId: s.nextId + 2,
      ...withConversation(s, projectId, (c) => ({
        busy: false,
        pending: null,
        messages: [
          ...c.messages,
          { id: s.nextId, role: "user", text: userText, failed: true },
          { id: s.nextId + 1, role: "assistant", text: errorText, failed: true },
        ],
      })),
    })),
  setProposalStatus: (projectId, proposalId, status, savedAs) =>
    set((s) =>
      withConversation(s, projectId, (c) => ({
        proposals: c.proposals.map((p) =>
          p.proposal.id === proposalId
            ? { ...p, status, ...(savedAs !== undefined ? { savedAs } : {}) }
            : p
        ),
      }))
    ),
  clear: (projectId) =>
    set((s) =>
      withConversation(s, projectId, () => ({
        messages: [],
        proposals: [],
        busy: false,
        pending: null,
      }))
    ),
}))

/** The conversation of `projectId` (the empty one until it has any state). */
export const selectConversation =
  (projectId: string) =>
  (s: AssistantState): Conversation =>
    conversationOf(s, projectId)

/**
 * The messages a turn sends: the conversation so far without failed turns,
 * then the new question, the last `MAX_SENT_MESSAGES` of them, oldest first.
 */
export const messagesToSend = (
  messages: AssistantMessage[],
  question: string
): AssistantTurnMessage[] =>
  [
    ...messages
      .filter((m) => !m.failed)
      .map((m): AssistantTurnMessage => ({ role: m.role, text: m.text })),
    { role: "user" as const, text: question },
  ].slice(-MAX_SENT_MESSAGES)
